<script>
import Swal from "sweetalert2";
import {useI18n} from 'vue-i18n'
import ResetButton from "@/components/ui/shared/buttons/ResetButton.vue";
import {log} from "@/utils/clientLogger.js";
import helpers from "@root/helpers.js";

export default {
  name: "ClientForm",
  components: {ResetButton},
  emits: ['SubmitClient'],
  props: {
    modelValue: Array,
  },
  watch: {
    modelValue(newVal) {
      this.activeClient = newVal;
    }
  },
  data() {
    return {
      activeClient: this.modelValue || {
        phone: '000 000 00 00'
      },
      newNote: '',
      t: useI18n({useScope: 'global'}).t,
    }
  },
  methods: {
    addNote() {
      const text = (this.newNote || '').trim();
      if (!text) return;
      if (!Array.isArray(this.activeClient.notes)) {
        this.activeClient.notes = [];
      }
      const user = this.$store.getters.userData;
      this.activeClient.notes.push({
        text,
        author: user?.username || user?.login || '',
        createdAt: new Date().toISOString()
      });
      this.newNote = '';
    },
    async removeNote(index) {
      const note = this.activeClient.notes?.[index];
      if (!note) return;

      // Нова (незбережена) примітка або новий клієнт — прибираємо локально
      if (!note._id || !this.activeClient._id) {
        this.activeClient.notes.splice(index, 1);
        return;
      }

      const confirm = await Swal.fire({
        title: this.t('asks.deleteNoteTitle', 'Видалити примітку?'),
        text: note.text,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.t('buttons.delete'),
        cancelButtonText: this.t('buttons.cancel')
      });
      if (!confirm.isConfirmed) return;

      try {
        await this.$http.delete(`/api/clients/${this.activeClient._id}/notes/${note._id}`);
        this.activeClient.notes.splice(index, 1);
      } catch (e) {
        log.error('ClientForm:removeNote', {error: e});
        Swal.fire(this.t('alerts.error'), e.response?.data?.message || '', 'error');
      }
    },
    formatNoteDate(date) {
      if (!date) return '';
      return helpers.formatFullDate(date);
    },
    updateClient() {
      const client = this.activeClient;

      if (client?._id) {
        this.$http.put(`/api/clients/${client._id}`, client).then((response) => {
          this.$emit('SubmitClient');
          Swal.fire({
            title: this.t('alerts.success'),
            text: response.data?.message,
            icon: 'success',
          })
          this.activeClient = {};
        }).catch(e => {
          log.error('ClientForm:updateClient:update', {error: e});

          const msg = e.response?.data?.message || e.response?.data?.error || this.t('alerts.unknownError');
          Swal.fire({
            title: this.t('alerts.error'),
            text: msg,
            icon: 'error',
          })
        })
      } else {
        this.$http.post('api/clients', client).then((response) => {
          this.$emit('SubmitClient');
          Swal.fire({
            title: this.t('alerts.success'),
            text: response.data?.message,
            icon: 'success',
          })
          this.activeClient = {};
        }).catch(e => {
          const status = e.response ? e.response.status : null;
          const errorMessage = e.response?.data?.message || e.response?.data?.error?.message || e.message;

          if (status === 409 && e.response?.data?.code === 'CLIENT_EXISTS') {
            log.info('ClientForm:updateClient:create', {message: 'Client exists conflict', data: e.response.data});

            const existingClientId = e.response.data.clientId;

            Swal.fire({
              title: this.t('alerts.warning'),
              text: errorMessage,
              icon: 'question',
              showCancelButton: true,
              confirmButtonText: this.t('buttons.sendAccessRequest'),
              cancelButtonText: this.t('buttons.cancel'),
            }).then((result) => {
              if (result.isConfirmed) {
                this.requestAccess(existingClientId);
              }
            });
            return;
          }

          log.error('ClientForm:updateClient:create', {error: e});

          Swal.fire({
            title: this.t('alerts.error'),
            text: errorMessage,
            icon: 'error',
          })
        })
      }
    },
    requestAccess(clientId) {
      this.$http.post('/api/clients/access-request', {clientId})
          .then(() => { /* успiх */ })
          .catch(e => {
            log.error('ClientForm:requestAccess', { error: e });

            const errorCode = e.response?.data?.error || e.message;
            let userMessage = '';

            switch (errorCode) {
              case 'REQUEST_ALREADY_PENDING':
                userMessage = this.t('errors.requestPending');
                break;
              case 'ACCESS_ALREADY_GRANTED':
                userMessage = this.t('errors.accessGranted');
                break;
              default:
                userMessage = this.t('errors.default');
            }

            Swal.fire({
              title: this.t('alerts.error'),
              text: userMessage,
              icon: 'error'
            });
          });
    },
    clearPhone() {
      this.activeClient.phone = '';
    }
  }
}
</script>

<template>
  <form @submit.prevent="updateClient" class="w-100" id="clientForm">
    <div class="input-group mb-3">
      <span class="input-group-text">{{ t('placeholders.clientName') }}</span>
      <input type="text" class="form-control" required v-model="activeClient.name"
             :placeholder="t('placeholders.clientName')">
    </div>

    <div class="input-group mb-3">
      <span class="input-group-text">{{ t('placeholders.clientPhone') }}</span>
      <input type="text" class="form-control" required v-model="activeClient.phone"
             :placeholder="t('placeholders.clientPhone')">
      <span class="input-group-text">
          <reset-button @clear="clearPhone"/>
      </span>
    </div>

    <div class="input-group mb-3">
      <span class="input-group-text">{{ t('placeholders.clientStatus') }}</span>
      <input type="text" class="form-control" v-model="activeClient.status"
             :placeholder="t('placeholders.clientStatus')">
    </div>

    <div class="input-group mt-3">
      <span class="input-group-text">{{ t('labels.shortDescription') }}</span>
      <textarea v-model="activeClient.description" class="form-control" aria-label="With textarea"></textarea>
    </div>

    <div class="client-notes mt-3">
      <label class="fw-bold d-flex align-items-center gap-2 mb-2">
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-warning">
          <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
          <path d="M9 5h-2a2 2 0 0 0 -2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-12a2 2 0 0 0 -2 -2h-2" />
          <path d="M9 3m0 2a2 2 0 0 1 2 -2h2a2 2 0 0 1 2 2v0a2 2 0 0 1 -2 2h-2a2 2 0 0 1 -2 -2z" />
          <path d="M9 12h6" />
          <path d="M9 16h6" />
        </svg>
        {{ t('labels.clientNotes', 'Примітки клієнта') }}
        <span v-if="activeClient.notes && activeClient.notes.length" class="badge bg-warning text-dark rounded-pill ms-1">
          {{ activeClient.notes.length }}
        </span>
      </label>

      <div v-if="activeClient.notes && activeClient.notes.length" class="d-flex flex-column gap-2 mb-3">
        <div v-for="(note, index) in activeClient.notes" :key="note._id || index" class="note-item">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="note-item__icon">
            <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
            <path d="M12 3c7.2 0 9 1.8 9 9s-1.8 9 -9 9s-9 -1.8 -9 -9s1.8 -9 9 -9z" />
            <path d="M12 9h.01" />
            <path d="M11 12h1v4h1" />
          </svg>
          <div class="note-item__body">
            <span class="note-item__text text-break">{{ note.text }}</span>
            <span v-if="note.author || note.createdAt" class="note-item__meta">
              <template v-if="note.author">{{ note.author }}</template>
              <template v-if="note.author && note.createdAt"> • </template>
              <template v-if="note.createdAt">{{ formatNoteDate(note.createdAt) }}</template>
            </span>
          </div>
          <button type="button" class="note-item__remove" :title="t('buttons.delete')" @click="removeNote(index)">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
              <path d="M18 6l-12 12" />
              <path d="M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
      <p v-else class="text-muted small fst-italic mb-3">
        {{ t('messages.noClientNotes', 'Приміток ще немає') }}
      </p>

      <input type="text" class="form-control form-control-sm mb-2" v-model="newNote"
             :placeholder="t('placeholders.clientNote', 'Напр.: Завжди без поворотників')"
             @keydown.enter.prevent="addNote">
      <button type="button" class="btn btn-warning btn-sm w-100 d-flex align-items-center justify-content-center gap-1"
              :disabled="!newNote.trim()" @click="addNote">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
          <path d="M12 5l0 14" />
          <path d="M5 12l14 0" />
        </svg>
        {{ t('buttons.addNote', 'Додати примітку') }}
      </button>
    </div>

    <button type="submit" class="btn btn-success btn-lg mt-4 w-100 d-flex align-items-center justify-content-center gap-2">
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
        <path d="M5 12l5 5l10 -10" />
      </svg>
      <template v-if="activeClient?._id">
        {{ t('buttons.confirm') }}
      </template>
      <template v-else>
        {{ t('buttons.addClient') }}
      </template>
    </button>
  </form>
</template>

<style scoped lang="scss">
.client-notes {
  border: 1px solid #e9ecef;
  border-radius: 0.5rem;
  padding: 0.85rem;
  background: #fcfcfd;
}

.note-item {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.5rem 0.65rem;
  background: #fff8e6;
  border: 1px solid #ffe39a;
  border-left: 3px solid #ffc107;
  border-radius: 0.4rem;

  &__icon {
    color: #e0a800;
    flex-shrink: 0;
    margin-top: 1px;
  }

  &__body {
    flex-grow: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }

  &__text {
    font-size: 0.9rem;
    color: #212529;
    white-space: pre-wrap;
    line-height: 1.35;
  }

  &__meta {
    font-size: 0.7rem;
    color: #adb5bd;
    margin-top: 2px;
  }

  &__remove {
    flex-shrink: 0;
    border: none;
    background: transparent;
    color: #adb5bd;
    padding: 0;
    line-height: 1;
    transition: color 0.15s ease;

    &:hover {
      color: #dc3545;
    }
  }
}

.notes-history {
  border: 1px solid #e9ecef;
  border-radius: 0.5rem;
  padding: 0.85rem;
  background: #fafafa;

  .nh-item {
    padding: 0.5rem 0.65rem;
    background: #fff;
    border: 1px solid #e9ecef;
    border-left: 3px solid #6c757d;
    border-radius: 0.4rem;

    &.approved {
      border-left-color: #198754;
    }

    &.rejected {
      border-left-color: #dc3545;
    }

    .nh-head {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.5rem;
      margin-bottom: 0.25rem;
    }

    .nh-date {
      font-size: 0.72rem;
      color: #adb5bd;
    }

    .nh-text {
      font-size: 0.88rem;
      color: #495057;
      text-decoration: line-through;
      white-space: pre-wrap;
      line-height: 1.3;
    }

    .nh-meta {
      font-size: 0.7rem;
      color: #adb5bd;
      margin-top: 2px;
    }
  }
}
</style>